import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGetRecommendationsQuery } from "../../store/api/genreApi";
import { MediaData } from "../../utils/types/app.types";
import Slider from "react-slick";

// Import slick-carousel styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

type SliderModuleProps = {
  id: string;
  mediaType: "movie" | "tv";
};

const SliderModule: React.FC<SliderModuleProps> = ({ id, mediaType }) => {
  const { data: recommended, isLoading, error } = useGetRecommendationsQuery({
    mediaType,
    id
  });
  const navigate = useNavigate();
  const sliderRef = useRef<Slider>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);

  const placeholderImg = "https://via.placeholder.com/500x750?text=No+Image";

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 5,
    slidesToScroll: 1,
    swipeToSlide: true,
    draggable: true,
    arrows: false, // We'll add custom arrows
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3 } },
      { breakpoint: 640, settings: { slidesToShow: 2 } },
    ],
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(false);
    setDragStartX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (Math.abs(e.clientX - dragStartX) > 10) {
      setIsDragging(true);
    }
  };

  const handleMouseUp = () => {
    // Reset dragging state after a short delay
    setTimeout(() => setIsDragging(false), 100);
  };

  const handleItemClick = (itemId: number) => {
    if (!isDragging) {
      navigate(`/${mediaType}-details/${itemId}`);
    }
  };

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  if (isLoading) {
    return (
      <section className="mt-8">
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          <p className="mt-2 text-gray-400">Loading recommendations...</p>
        </div>
      </section>
    );
  }

  if (error || !recommended || recommended.length === 0) {
    return null;
  }

  return (
    <section className="mt-8">
      <h3 className="text-xl font-semibold mb-4">
        🎯 Recommended {mediaType === "tv" ? "TV Shows" : "Movies"}
      </h3>

      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={goToPrev}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={goToNext}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <Slider ref={sliderRef} {...settings}>
          {recommended.map((item: MediaData) => (
            <div
              key={item.id}
              className="px-2"
            >
              <div 
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onClick={() => handleItemClick(item.id)}
                className="cursor-pointer"
              >
                <img
                  src={
                    item.poster_path
                      ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
                      : placeholderImg
                  }
                  alt={item.title || item.name || "Untitled"}
                  className="rounded-lg h-[240px] w-full object-cover"
                  draggable={false}
                />
                <div className="p-2 text-white text-sm truncate text-center">
                  {item.title || item.name || "Untitled"}
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default SliderModule;